import { Link } from "react-router-dom";
import { MapPin, Clock, Phone, ShoppingBag, ArrowRight, Check, Smartphone } from "lucide-react";
import { SITE, IMAGES } from "@/lib/config";
import { useLang } from "@/lib/i18n";
import { getOpenStatus } from "@/lib/hours";
import { OrderButton, CallLink, DirectionsLink, TrackedLink } from "@/components/TrackedLinks";
import { RatingBadge } from "@/components/Reviews";

export const Hero = () => {
  const { t } = useLang();
  return (
    <section className="relative min-h-[82vh] flex items-end md:items-center" data-testid="hero-section">
      <picture className="absolute inset-0">
        <source media="(max-width: 640px)" srcSet={IMAGES.heroMobile} />
        <img src={IMAGES.hero} alt="Quesabirria tacos with melted cheese, birria and a cup of consomé at Tijuas Tacos Birrieria" fetchPriority="high" decoding="async" width="1536" height="2048" className="w-full h-full object-cover object-[50%_60%]" />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-t from-hacienda-charcoal/90 via-hacienda-charcoal/45 to-hacienda-charcoal/20 md:bg-gradient-to-r md:from-hacienda-charcoal/85 md:via-hacienda-charcoal/50 md:to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 w-full">
        <div className="max-w-2xl">
          <p className="text-hacienda-amber text-sm uppercase tracking-[0.3em] mb-5 animate-fade-in-up">{t("hero.eyebrow")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white font-semibold mb-6 leading-[1.05] animate-fade-in-up animation-delay-100">{t("hero.title")}</h1>
          <p className="text-hacienda-cream/90 text-lg md:text-xl mb-10 leading-relaxed animate-fade-in-up animation-delay-200">{t("hero.sub")}</p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in-up animation-delay-300">
            <OrderButton location="hero" size="lg" data-testid="hero-order-btn"><ShoppingBag size={20} aria-hidden="true" /> {t("actions.order")}</OrderButton>
            <Link to="/menu" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white text-white hover:bg-white hover:text-hacienda-charcoal px-9 py-4 text-lg font-medium transition-colors" data-testid="hero-menu-link">{t("actions.viewMenu")}</Link>
          </div>
          <Link to="/catering" className="inline-flex items-center gap-2 mt-6 text-hacienda-cream hover:text-hacienda-amber underline underline-offset-4 font-medium transition-colors animate-fade-in-up animation-delay-300" data-testid="hero-catering-link">
            {t("actions.quote")} <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const InfoItem = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-3" data-testid={testId}>
    <div className="bg-hacienda-terracotta/10 p-2.5 rounded-lg flex-shrink-0"><Icon size={20} className="text-hacienda-terracotta" aria-hidden="true" /></div>
    <div>
      <p className="text-xs uppercase tracking-wider text-hacienda-gray mb-0.5">{label}</p>
      <div className="text-hacienda-charcoal font-medium">{children}</div>
    </div>
  </div>
);

const OpenStatus = () => {
  const { t, lang } = useLang();
  const s = getOpenStatus(lang);
  return (
    <span data-testid="open-status">
      <span className={`inline-flex items-center gap-1.5 font-semibold ${s.isOpen ? "text-green-700" : "text-hacienda-terracotta"}`}>
        <span className={`w-2 h-2 rounded-full ${s.isOpen ? "bg-green-600" : "bg-hacienda-terracotta"}`} aria-hidden="true" />
        {s.isOpen ? `${t("info.openNow")} · ${t("info.until")} ${s.closesAt}` : `${t("info.closedNow")}${s.nextOpen ? ` · ${t("info.opens")} ${s.nextOpen}` : ""}`}
      </span>
      <span className="block text-sm text-hacienda-gray font-normal">{t("info.today")}: {s.todayHours || t("days.closed")}</span>
    </span>
  );
};

const ghostCls = "!p-0 !text-hacienda-charcoal hover:!text-hacienda-terracotta !justify-start text-left !rounded-none";

export const InfoStrip = () => {
  const { t } = useLang();
  return (
    <section className="bg-white border-b border-hacienda-border-light" data-testid="info-strip">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="font-serif text-2xl md:text-3xl text-hacienda-charcoal font-semibold mb-6 italic">{t("hero.eyebrow")}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <InfoItem icon={MapPin} label={t("visit.address")} testId="info-address">
            <DirectionsLink location="info_strip" variant="ghost" size="sm" className={ghostCls}>{SITE.address.street}, {SITE.address.city}</DirectionsLink>
          </InfoItem>
          <InfoItem icon={Clock} label={t("visit.hours")} testId="info-hours"><OpenStatus /></InfoItem>
          <InfoItem icon={Phone} label={t("visit.phone")} testId="info-phone">
            <CallLink location="info_strip" variant="ghost" size="sm" className={ghostCls}>{SITE.phoneDisplay}</CallLink>
          </InfoItem>
          <InfoItem icon={ShoppingBag} label={t("info.takeout")} testId="info-takeout"><RatingBadge compact /></InfoItem>
        </div>
      </div>
    </section>
  );
};

export const WhySection = () => {
  const { t } = useLang();
  return (
    <section className="py-24 px-4" data-testid="why-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-2 lg:sticky lg:top-28">
          <p className="text-hacienda-terracotta text-sm uppercase tracking-[0.2em] mb-4">{t("why.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-hacienda-charcoal font-semibold leading-tight mb-6">{t("why.title")}</h2>
          <img src={IMAGES.tacosDorados} alt="Crispy tacos dorados with birria and consomé" loading="lazy" decoding="async" width="600" height="840" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/5] hidden lg:block" />
        </div>
        <ul className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
          {t("why.items").map(([title, desc], i) => (
            <li key={title} className="bg-hacienda-cream rounded-2xl p-6 border border-hacienda-border-light" data-testid={`why-item-${i}`}>
              <div className="w-9 h-9 rounded-full bg-hacienda-terracotta text-white flex items-center justify-center mb-4"><Check size={18} aria-hidden="true" /></div>
              <h3 className="font-semibold text-hacienda-charcoal text-lg mb-1.5">{title}</h3>
              <p className="text-hacienda-gray text-sm leading-relaxed">{desc}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export const CtaSection = () => {
  const { t } = useLang();
  const [appTitle, appCopy] = t("announcement.text").split("—");
  return (
    <section className="py-20 px-4" data-testid="cta-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="bg-hacienda-charcoal text-hacienda-cream rounded-3xl p-10 flex flex-col">
          <Smartphone size={32} className="text-hacienda-amber mb-4" aria-hidden="true" />
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-3">{appTitle}</h2>
          <p className="text-hacienda-cream/80 mb-8 flex-grow">{appCopy}</p>
          <TrackedLink href={SITE.appStoreUrl} event="app_download_click" params={{ location: "home_cta" }} variant="light" className="self-start" data-testid="home-app-btn">{t("announcement.cta")}</TrackedLink>
        </div>
        <div className="relative rounded-3xl overflow-hidden min-h-[320px] flex flex-col justify-end p-10">
          <img src={IMAGES.birriaPizza} alt="Birria pizza for catering" loading="lazy" decoding="async" width="600" height="840" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-hacienda-charcoal/90 to-hacienda-charcoal/10" />
          <div className="relative">
            <h2 className="font-serif text-3xl md:text-4xl text-white font-semibold mb-3">{t("catering.title")}</h2>
            <Link to="/catering" className="inline-flex items-center gap-2 rounded-full bg-hacienda-terracotta hover:bg-hacienda-terracotta-dark text-white px-7 py-3 font-medium transition-colors" data-testid="home-catering-btn">
              {t("actions.quote")} <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
